import { useState, useEffect, useRef } from 'react'
import styled from 'styled-components'

import { useTheme } from '@mui/material'
import Modal from '@mui/material/Modal'
import Box from '@mui/material/Box'
import Grid from '@mui/material/Grid'
import Button from '@mui/material/Button'
import LinearProgress from '@mui/material/LinearProgress'

import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import ErrorIcon from '@mui/icons-material/Error'
import ThumbUpIcon from '@mui/icons-material/ThumbUp';

import { request } from "@/Api"
import { TargetsPass, TargetsWarning } from "@/common/StyledComponents"

const CustomBox = styled(Box)`
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 50%;
    padding: 24px;
    border-radius: 20px;
`
const Title = styled.h2`
    margin: ${props => props.center ? "24px 0 24px 0" : "0 0 24px 0"};
    font-size: 28px;
    font-weight: normal;
    text-align: ${props => props.center ? "center" : "left"};
`
const Subtitle = styled.h3`
    margin: 2px 0 24px 0;
    font-size: 14px;
    font-weight: 600;
`

export default function BidDeepCalculate({ bidId, open, setOpen, hidePass }){
    const theme = useTheme()
    
    const [ deepCalcWarnings, setDeepCalcWarnings ] = useState([])
    const [ deepCalcProgress, setDeepCalcProgress ] = useState(0)

    // Using refs to avoid closure issues in setInterval below
    const deepCalculatingRef = useRef(true)
    const hidePassRef = useRef(false)

    useEffect(() => {
        setDeepCalcProgress(0)
        setDeepCalcWarnings([])

        if (open) {
            deepCalculatingRef.current = true

            request.put(`/bids/${bidId}/deep-calculate`).then(response => {
                setDeepCalcWarnings(response.data)
                hidePassRef.current = hidePass && response.data.length === 0
            }).catch(error => {
                setDeepCalcWarnings(["There was an error deep calculating your bid, please contact an admin."])
            }).finally(() => {
                deepCalculatingRef.current = false
            })
            
            const speed = 4
            const interval = setInterval(() => {
               setDeepCalcProgress(prev => {
                   // Waiting for deep calculation request to finish
                    if (deepCalculatingRef.current && prev >= 80) {
                        return prev
                    }
                    if (prev >= 100 - speed) {
                        clearInterval(interval)

                        if (hidePassRef.current) {
                            setOpen(false)
                            return 99 // This prevents brief flash of the success modal
                        }

                        return 100
                    }
                    return prev + speed
                })
            }, 100)

            return () => {
                clearInterval(interval)
            }
        }
    }, [open])

    const handleClose = () => {
        if (deepCalcProgress < 100) {
            return
        }
        setOpen(false)
    }

    return (
        <Modal
            sx={{overflow: "scroll"}}
            open={open}
            onClose={handleClose}
        >
            {deepCalcProgress < 100 ? (
                <CustomBox sx={{backgroundColor: theme.palette.background.default}}>
                    <Title center>Deep calculating...</Title>
                    <LinearProgress variant="determinate" value={deepCalcProgress} sx={{margin: "48px 24px 36px 24px"}} />
                </CustomBox>
            ) : (
                <CustomBox sx={{backgroundColor: theme.palette.background.default}}>
                    <Title>Deep Calculate Results</Title>
                    
                    { deepCalcWarnings.length > 0 ? (   
                        <TargetsWarning>
                            <Grid container spacing={1}>
                                <Grid item><ErrorIcon color="warning" /></Grid>
                                <Grid item>
                                    <Subtitle>Heads up!</Subtitle>
                                    <p>We found a few inconsistencies on your bid, but don't worry we've automatically adjusted them.</p>
                                    <ul>
                                        { deepCalcWarnings.map((x, i) => (
                                            <li key={i} style={x.object.startsWith("bid") ? {fontWeight: "bold"} : {}}>
                                                {x.object}
                                                <ul>
                                                    {x.changes.map((change, j) => (
                                                        <li key={j}>{change}</li>
                                                    )) }    
                                                </ul>
                                            </li>
                                        )) }
                                    </ul>
                                </Grid>
                            </Grid>
                        </TargetsWarning>
                    ) : (   
                        <TargetsPass>
                            <Grid container spacing={1}>
                                <Grid item><CheckCircleIcon color="success" /></Grid>
                                <Grid item>
                                    <Subtitle>Good stuff</Subtitle>
                                    <p>We didn't find any inconsistencies on your bid.</p>
                                </Grid>
                            </Grid>
                        </TargetsPass>
                    ) }

                    <Grid container spacing={2} alignItems="center" sx={{marginTop: "12px"}}>
                        <Grid item flexGrow={1}>
                            <Button
                                fullWidth
                                color="primary"
                                sx={{color: "black"}}
                                variant="contained"
                                onClick={handleClose}
                                startIcon={<ThumbUpIcon />}
                            >
                                Continue
                            </Button>
                        </Grid>
                    </Grid>
                </CustomBox>
            )}
        </Modal>
    )
}
